import { CART_ITEM_QUANTITY_MAX } from '@/lib/cart-quantity'
import type { CartItem, Product } from '@/lib/types'

const QUICK_CHECKOUT_CART_VERSION = 'v1'

const buildStorageKey = (lojaId?: number | null) =>
  ['b2b-commerce', 'quick-checkout-cart', QUICK_CHECKOUT_CART_VERSION, lojaId && lojaId > 0 ? lojaId : 'default'].join(':')

const normalizeQuantity = (quantity: unknown, minOrder = 1) => {
  const parsed = Number(quantity)
  if (!Number.isFinite(parsed)) return minOrder
  return Math.max(minOrder, Math.min(CART_ITEM_QUANTITY_MAX, Math.trunc(parsed)))
}

const mergeItems = (items: CartItem[]) => {
  const merged: CartItem[] = []
  const indexByProductId = new Map<string, number>()

  for (const item of items) {
    const productId = item.product?.id
    if (!productId) continue
    const quantity = normalizeQuantity(item.quantity, item.product.minOrder ?? 1)
    const existingIndex = indexByProductId.get(productId)
    if (typeof existingIndex === 'number') {
      const existing = merged[existingIndex]
      existing.quantity = Math.min(CART_ITEM_QUANTITY_MAX, existing.quantity + quantity)
      continue
    }
    indexByProductId.set(productId, merged.length)
    merged.push({
      product: item.product,
      quantity,
      checkoutSource: 'cart',
      sourceProductGradeId: item.sourceProductGradeId,
    })
  }

  return merged
}

export function loadQuickCheckoutCartItems(lojaId?: number | null): CartItem[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(buildStorageKey(lojaId))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return mergeItems(parsed as CartItem[])
  } catch {
    return []
  }
}

export function saveQuickCheckoutCartItems(lojaId: number | null | undefined, items: CartItem[]) {
  if (typeof window === 'undefined') return
  const merged = mergeItems(items)
  const key = buildStorageKey(lojaId)
  try {
    if (merged.length === 0) {
      window.localStorage.removeItem(key)
      return
    }
    window.localStorage.setItem(key, JSON.stringify(merged))
  } catch {
    // Storage cheio ou indisponível não deve quebrar navegação da loja.
  }
}

export function upsertQuickCheckoutCartItem(
  lojaId: number | null | undefined,
  currentItems: CartItem[],
  product: Product,
  quantity: number,
) {
  const nextItems = mergeItems([
    ...currentItems,
    {
      product,
      quantity: normalizeQuantity(quantity, product.minOrder ?? 1),
      checkoutSource: 'cart',
    },
  ])
  saveQuickCheckoutCartItems(lojaId, nextItems)
  return nextItems
}

export function setQuickCheckoutCartItemQuantity(
  lojaId: number | null | undefined,
  currentItems: CartItem[],
  productId: string,
  quantity: number,
) {
  const nextItems = mergeItems(
    currentItems.map((item) => (
      item.product.id === productId
        ? { ...item, quantity: normalizeQuantity(quantity, item.product.minOrder ?? 1) }
        : item
    ))
  )
  saveQuickCheckoutCartItems(lojaId, nextItems)
  return nextItems
}

export function removeQuickCheckoutCartItem(
  lojaId: number | null | undefined,
  currentItems: CartItem[],
  productId: string,
) {
  const nextItems = mergeItems(currentItems.filter((item) => item.product.id !== productId))
  saveQuickCheckoutCartItems(lojaId, nextItems)
  return nextItems
}

export function clearQuickCheckoutCartItems(lojaId?: number | null) {
  saveQuickCheckoutCartItems(lojaId, [])
}
